/**
 * SiteLayout — restaurant-tenant chrome wrapper.
 *
 * The HEADER is delegated to the global <PlatformHeader/> snippet (Rule #4
 * — one header, every module, every domain). This file only owns the
 * page shell + <ClaimListingBanner/> + footer.
 *
 * The <GoogleTranslateBar/> is mounted ONCE at the App.js root so every
 * page (storefronts, sales pages, tenant pages, meetup) gets it without
 * re-import. Do NOT add it here.
 *
 * DO NOT add an inline header block here — extend PlatformHeader props
 * instead. See /app/memory/AGENT_RULES.md.
 */
import { Outlet, Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube, Linkedin } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useBusiness } from "@/context/BusinessContext";
import PlatformHeader from "@/components/PlatformHeader";
import SeoHead from "@/components/SeoHead";
import ClaimListingBanner from "@/components/ClaimListingBanner";
import { useT } from "@/lib/useT";
import { getModuleHost } from "@/lib/moduleHost";

// Renders only the socials that have a non-empty URL. Falls back to a generic globe
// icon for platforms without a dedicated lucide icon (tiktok / pinterest / threads / whatsapp).
function SocialIconRow({ socials }) {
  if (!socials) return null;
  const items = [
    { key: "facebook",  Icon: Facebook,  label: "Facebook"  },
    { key: "instagram", Icon: Instagram, label: "Instagram" },
    { key: "twitter",   Icon: Twitter,   label: "X / Twitter" },
    { key: "youtube",   Icon: Youtube,   label: "YouTube"   },
    { key: "linkedin",  Icon: Linkedin,  label: "LinkedIn"  },
    { key: "tiktok",    Icon: null,      label: "TikTok",    glyph: "T"  },
    { key: "pinterest", Icon: null,      label: "Pinterest", glyph: "P"  },
    { key: "threads",   Icon: null,      label: "Threads",   glyph: "@"  },
    { key: "whatsapp",  Icon: null,      label: "WhatsApp",  glyph: "W"  },
  ].filter((it) => (socials?.[it.key] || "").trim());
  if (items.length === 0) return null;
  return (
    <div data-testid="footer-socials" className="mt-4 flex flex-wrap gap-2">
      {items.map(({ key, Icon, label, glyph }) => (
        <a
          key={key}
          href={socials[key]}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`footer-social-${key}`}
          aria-label={label}
          title={label}
          className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 text-[#FDFBF7] transition-colors"
        >
          {Icon ? <Icon className="h-4 w-4" strokeWidth={1.5} /> : <span className="text-xs font-bold">{glyph}</span>}
        </a>
      ))}
    </div>
  );
}

export default function SiteLayout({ children }) {
  const settings = useSettings();
  const { business } = useBusiness();
  const tr = useT();
  const moduleBrand = typeof window !== "undefined" ? getModuleHost() : null;
  const isRecipesHost = moduleBrand?.key === "recipes";
  const brandName = isRecipesHost
    ? "HustleRecipes"
    : (business?.business_name || settings?.restaurant_name || "");
  const tagline = tr(business, "tagline");
  const featBookings = business?.features?.table_bookings !== false;
  const ctaLabel = isRecipesHost
    ? "Explore recipes"
    : (tr(business, "cta_label") || "Reserve");
  const ctaLink = isRecipesHost ? "/recipes" : (business?.cta_link || "/book");

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7] dark:bg-stone-950 text-[#121614] dark:text-stone-100">
      <SeoHead />
      <ClaimListingBanner />

      {/* ONE header for every module — see /app/memory/AGENT_RULES.md Rule #4 */}
      <PlatformHeader />

      <main className="flex-1">
        {children ?? <Outlet />}
      </main>

      <footer data-testid="site-footer" className="bg-[#121614] text-[#FDFBF7] mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div>
              <p className="font-serif-display text-3xl mb-3">
                {brandName}
              </p>
              <p className="text-sm text-[#FDFBF7]/70 leading-relaxed">
                {business?.tagline || settings?.tagline ?
                  (tagline || business?.tagline || settings?.tagline) :
                  "Authentic Italian cuisine, Australian wines, and slow hospitality. Made with love since 2008."}
              </p>
              <SocialIconRow socials={business?.socials} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247] mb-3">Visit</p>
              <p
                data-testid="footer-address"
                className="text-sm text-[#FDFBF7]/80 leading-relaxed whitespace-pre-line"
              >
                {business?.address || settings?.address || ""}
              </p>
              {(business?.phone || settings?.phone) && (
                <p data-testid="footer-phone" className="text-sm text-[#FDFBF7]/80 mt-2">
                  {business?.phone || settings?.phone}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247] mb-3">Hours</p>
              <p
                data-testid="footer-hours"
                className="text-sm text-[#FDFBF7]/80 leading-relaxed whitespace-pre-line"
              >
                {business?.hours || settings?.hours ||
                  "Tue–Thu · 5pm – 10pm\nFri–Sat · 12pm – 11pm\nSun · 12pm – 9pm"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#5A7247] mb-3">Explore</p>
              <ul className="space-y-1.5 text-sm text-[#FDFBF7]/80">
                <li><Link to="/menu" className="hover:text-white">Menu</Link></li>
                {featBookings && (
                  <li>
                    <Link to={ctaLink} data-testid="footer-cta-link" className="hover:text-white">
                      {ctaLabel}
                    </Link>
                  </li>
                )}
                <li><Link to="/reviews" data-testid="footer-reviews-link" className="hover:text-white">Reviews</Link></li>
                <li><Link to="/blog" data-testid="footer-blog-link" className="hover:text-white">Journal</Link></li>
                <li><Link to="/account" className="hover:text-white">My account</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-[#FDFBF7]/50">
            <p>© {new Date().getFullYear()} {brandName}. All rights reserved.</p>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              <Link to="/about" data-testid="footer-about-link" className="hover:text-white">About</Link>
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white">Terms</Link>
              <Link to="/credits" data-testid="footer-credits-link" className="hover:text-white">Credits</Link>
              <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white">Staff Login</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
